import type {
    AstView,
    Document,
    Version,
    Project,
    CSSObject,
    PageState,
} from 'rendition-core';
import isEmpty from 'lodash/isEmpty';
import axios from 'axios';
import {StyleGuide} from 'Views/Styleguide/hooks/useStyleguideData';

export const loadDocument = async (id: string) => {
    const result = await axios.get<{doc: Document; latestVersion: Version}>(
        `/compiler/${id}`
    );
    return result;
};

export async function sendToBackgroundApiCall(
    view: AstView,
    parentId: string,
    childToSendId: string
) {
    const res = await axios.post<{view: AstView}>(
        `/compiler/merge-background`,
        {
            view: view,
            mergeIntoId: parentId,
            childrenToFlattenIds: [childToSendId],
        }
    );
    return res.data.view;
}

export function saveDocument(id: string, view: AstView) {
    return axios.post(`/compiler/update-latest-version/${id}`, {view});
}

export function compileDocument(id: string) {
    return axios.post(`compiler/combined-compiler/${id}`);
}

// TODO: We probably want to refactor this to be combined with auto save functionality and save and
// API call
export function updateWebflowJson(id: string, view: AstView) {
    return axios.patch<{webflowJson: string}>(
        `compiler/update-webflow-json/${id}`,
        {view}
    );
}

export function cloneDocument(id: string) {
    return axios.get<Document>(`/compiler/clone/${id}`);
}

export function getZippedAssets(view: AstView) {
    return axios.post<{view: AstView}>('/compiler/zip-assets', {
        view: view,
    });
}

export async function getCompiles({
    userId,
    page = 0,
}: {userId: string; page?: number}) {
    const res = await axios.get<{compiles: Document[]}>('/user/compiles', {
        params: {offset: 10 * page, limit: 10, userId},
    });

    return res.data.compiles;
}

export async function getAIHistory({
    userId,
    page = 0,
}: {userId: string; page?: number}) {
    const res = await axios.get<{aiHistory: PageState[]}>('/user/ai-history', {
        params: {offset: 10 * page, limit: 10, userId},
    });

    return res.data.aiHistory;
}

export async function getProject(userId: string) {
    const result = await axios.get<{project: Project}>('/project', {
        params: {userId: userId},
    });
    return result.data.project;
}

export async function updateStyleGuide(userId: string, styleGuide: StyleGuide) {
    const styleGuideWithoutEmptyClasses: StyleGuide = {
        cssClasses: {},
        cssClassMeta: {},
    };

    if (styleGuide.cssClasses) {
        Object.keys(styleGuide.cssClasses).forEach((key) => {
            const cssClass = styleGuide.cssClasses?.[key];
            if (!isEmpty(cssClass)) {
                styleGuideWithoutEmptyClasses.cssClasses = {
                    ...styleGuideWithoutEmptyClasses.cssClasses,
                    [key]: styleGuide.cssClasses?.[key]!,
                };
                styleGuideWithoutEmptyClasses.cssClassMeta = {
                    ...styleGuideWithoutEmptyClasses.cssClassMeta,
                    [key]: styleGuide.cssClassMeta?.[key]!,
                };
            }
        });
    }

    const result = await axios.patch<{
        project: Project;
        message: 'OK';
        status: 200;
    }>('/project/styleguide', {
        userId,
        styleguide: styleGuideWithoutEmptyClasses,
    });
    return result.data.project;
}

export async function updateDefaultCSS(userId: string, defaultCSS: CSSObject) {
    const result = await axios.patch<{
        project: Project;
        message: 'OK';
        status: 200;
    }>('/project/default-css', {
        userId,
        defaultCSS,
    });
    return result.data.project;
}

export * from './user';
export * from './tailwind';
export * from './GPTCodeGen';
export * from './stripe';
export * from './project';
export * from './embeddings';
